import { useEffect, useRef, useState } from 'react';
import { Calendar } from 'lucide-react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import type { PeriodPreset } from '@contracts';
import { useFilters } from '@/lib/filterContext';
import { resolvePeriod } from '@/lib/period';

const QUICK: { value: PeriodPreset; label: string }[] = [
  { value: 'today', label: 'Hoy' },
  { value: 'yesterday', label: 'Ayer' },
  { value: 'this_week', label: 'Semana' },
  { value: 'this_month', label: 'Este mes' },
  { value: 'last_month', label: 'Mes ant.' },
  { value: 'last_7d', label: '7 días' },
  { value: 'last_30d', label: '30 días' },
  { value: 'ytd', label: 'Este año' },
];

const LABEL: Record<string, string> = Object.fromEntries(QUICK.map((q) => [q.value, q.label]));

export function DateFilter() {
  const { period, setPeriod } = useFilters();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const h = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const { from, to } = resolvePeriod(period);
  const isPreset = typeof period === 'string';
  const triggerLabel = isPreset ? (LABEL[period] ?? 'Período') : 'Personalizado';
  const longFmt = (d: string) => format(new Date(`${d}T00:00:00`), "EEEE, d 'de' MMMM yyyy", { locale: es });

  return (
    <div className="relative" ref={ref}>
      <div className="flex items-center gap-1.5">
        <span className="rounded-lg border border-slate-300 px-3 py-1.5 text-sm font-medium text-slate-600">
          {triggerLabel}
        </span>
        <button
          onClick={() => setOpen((o) => !o)}
          title="Período"
          className="grid h-8 w-8 place-items-center rounded-lg bg-red-600 text-white hover:bg-red-700"
        >
          <Calendar className="h-4 w-4" />
        </button>
      </div>

      {open && (
        <div className="absolute left-0 z-40 mt-1.5 w-80 rounded-2xl border border-slate-200 bg-white p-4 shadow-2xl">
          <div className="mb-3 flex items-center gap-2">
            <span className="grid h-7 w-7 place-items-center rounded-lg bg-red-50 text-red-600">
              <Calendar className="h-4 w-4" />
            </span>
            <h3 className="text-sm font-bold text-slate-800">Período rápido</h3>
          </div>

          <div className="grid grid-cols-4 gap-1.5">
            {QUICK.map((q) => {
              const active = isPreset && period === q.value;
              return (
                <button
                  key={q.value}
                  onClick={() => setPeriod(q.value)}
                  className={`rounded-lg px-2 py-1.5 text-xs font-semibold transition ${
                    active ? 'bg-red-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                >
                  {q.label}
                </button>
              );
            })}
          </div>

          <div className="my-3 rounded-xl bg-red-50 px-3 py-2 text-center">
            <p className="text-[10px] font-bold uppercase tracking-wide text-slate-400">Rango aplicado</p>
            <p className="mt-0.5 text-xs font-semibold capitalize text-red-700">
              {longFmt(from)} <span className="text-slate-400">→</span> {longFmt(to)}
            </p>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1 block text-[10px] font-bold uppercase tracking-wide text-slate-400">Desde</label>
              <input
                type="date"
                value={from}
                max={to}
                onChange={(e) => setPeriod({ from: e.target.value, to })}
                className="w-full rounded-lg border border-slate-300 px-2 py-1.5 text-sm outline-none focus:border-red-500"
              />
            </div>
            <div>
              <label className="mb-1 block text-[10px] font-bold uppercase tracking-wide text-slate-400">Hasta</label>
              <input
                type="date"
                value={to}
                min={from}
                onChange={(e) => setPeriod({ from, to: e.target.value })}
                className="w-full rounded-lg border border-slate-300 px-2 py-1.5 text-sm outline-none focus:border-red-500"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
